/**
 * VAN MILE rails and tabs (homepage product rails, fight gear rail).
 *
 * <vm-rail>: a horizontal scroll-snap list with previous/next buttons.
 *   <vm-rail class="vm-rail">
 *     <ul class="vm-rail__track" id="Track-x" data-rail-track>…<li class="vm-rail__item">…</li></ul>
 *     <div class="vm-rail__controls" data-rail-controls hidden>
 *       <button type="button" data-rail-prev aria-controls="Track-x">…</button>
 *       <button type="button" data-rail-next aria-controls="Track-x">…</button>
 *     </div>
 *   </vm-rail>
 *   Controls stay hidden until the track overflows, and each button is disabled at its end.
 *
 * <vm-tabs>: an ARIA tablist (automatic activation, arrow keys, Home/End) that toggles tab panels.
 *   Tabs are buttons with role="tab", aria-selected and aria-controls; panels use role="tabpanel" + hidden.
 */
(() => {
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (!customElements.get('vm-rail')) {
    class VmRail extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-rail-track]');
        if (!this.track) return;
        this.prev = this.querySelector('[data-rail-prev]');
        this.next = this.querySelector('[data-rail-next]');
        this.controls = this.querySelector('[data-rail-controls]');

        this.onScroll = this.onScroll.bind(this);
        this.onPrev = () => this.go(-1);
        this.onNext = () => this.go(1);
        this.track.addEventListener('scroll', this.onScroll, { passive: true });
        this.prev?.addEventListener('click', this.onPrev);
        this.next?.addEventListener('click', this.onNext);

        // Also fires when a hidden tab panel becomes visible (its width goes from 0 to real).
        if ('ResizeObserver' in window) {
          this.observer = new ResizeObserver(() => this.update());
          this.observer.observe(this.track);
        }
        this.update();
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.track?.removeEventListener('scroll', this.onScroll);
        this.prev?.removeEventListener('click', this.onPrev);
        this.next?.removeEventListener('click', this.onNext);
      }

      onScroll() {
        if (this.ticking) return;
        this.ticking = true;
        requestAnimationFrame(() => {
          this.ticking = false;
          this.update();
        });
      }

      /** Width of the cards that are fully visible, so one click moves one "page" of cards. */
      pageSize() {
        const item = this.track.querySelector('.vm-rail__item');
        if (!item) return this.track.clientWidth;
        const styles = getComputedStyle(this.track);
        const gap = parseFloat(styles.columnGap) || 0;
        const padding = (parseFloat(styles.paddingLeft) || 0) + (parseFloat(styles.paddingRight) || 0);
        const step = item.getBoundingClientRect().width + gap;
        const visible = Math.max(1, Math.floor((this.track.clientWidth - padding + gap) / step));
        return visible * step;
      }

      go(direction) {
        const rtl = getComputedStyle(this.track).direction === 'rtl';
        this.track.scrollBy({
          left: direction * (rtl ? -1 : 1) * this.pageSize(),
          behavior: reduceMotion() ? 'auto' : 'smooth',
        });
      }

      update() {
        const { scrollWidth, clientWidth } = this.track;
        const max = scrollWidth - clientWidth;
        const position = Math.abs(this.track.scrollLeft);
        const overflows = clientWidth > 0 && max > 2;
        if (this.controls) this.controls.hidden = !overflows;
        if (!this.prev || !this.next) return;

        const atStart = position <= 2;
        const atEnd = position >= max - 2;
        // Keep keyboard focus on a usable button when the focused one is about to be disabled.
        if (atStart && document.activeElement === this.prev && !atEnd) this.next.focus();
        if (atEnd && document.activeElement === this.next && !atStart) this.prev.focus();
        this.prev.disabled = !overflows || atStart;
        this.next.disabled = !overflows || atEnd;
      }
    }
    customElements.define('vm-rail', VmRail);
  }

  if (!customElements.get('vm-tabs')) {
    class VmTabs extends HTMLElement {
      connectedCallback() {
        this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
        if (!this.tabs.length) return;
        this.onClick = (event) => {
          const tab = event.target.closest('[role="tab"]');
          if (tab && this.tabs.includes(tab)) this.select(tab);
        };
        this.onKeydown = this.onKeydown.bind(this);
        this.onBlockSelect = (event) => {
          // Theme editor: selecting a tab block shows its panel.
          const tab = event.target.closest('[role="tab"]');
          if (tab && this.tabs.includes(tab)) this.select(tab);
        };
        this.addEventListener('click', this.onClick);
        this.addEventListener('keydown', this.onKeydown);
        this.addEventListener('shopify:block:select', this.onBlockSelect);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        this.removeEventListener('keydown', this.onKeydown);
        this.removeEventListener('shopify:block:select', this.onBlockSelect);
      }

      select(tab, focus = false) {
        this.tabs.forEach((item) => {
          const active = item === tab;
          item.setAttribute('aria-selected', active ? 'true' : 'false');
          item.tabIndex = active ? 0 : -1;
          const panel = document.getElementById(item.getAttribute('aria-controls'));
          if (panel) panel.hidden = !active;
        });
        if (focus) tab.focus();
      }

      onKeydown(event) {
        const tab = event.target.closest('[role="tab"]');
        if (!tab || !this.tabs.includes(tab)) return;
        const index = this.tabs.indexOf(tab);
        const last = this.tabs.length - 1;
        let target = null;
        switch (event.key) {
          case 'ArrowRight':
            target = index === last ? 0 : index + 1;
            break;
          case 'ArrowLeft':
            target = index === 0 ? last : index - 1;
            break;
          case 'Home':
            target = 0;
            break;
          case 'End':
            target = last;
            break;
          default:
            return;
        }
        event.preventDefault();
        this.select(this.tabs[target], true);
      }
    }
    customElements.define('vm-tabs', VmTabs);
  }
})();
